/** Client-side image compression.
 *  Reads a File/Blob, resizes so the longest edge <= 1280px, encodes JPEG at 70%,
 *  and returns a new Blob (~200-300KB for typical phone photos).
 */
export async function compressImage(file, { maxEdge = 1280, quality = 0.7 } = {}) {
  if (!file) return null;
  // Skip if already small enough
  if (file.size <= 250 * 1024 && (file.type === "image/jpeg" || file.type === "image/webp")) {
    return file;
  }
  const imgBitmap = await createBitmap(file);
  const { width, height } = fitInto(imgBitmap.width, imgBitmap.height, maxEdge);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(imgBitmap, 0, 0, width, height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  return blob || file;
}

function fitInto(w, h, maxEdge) {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  const ratio = w >= h ? maxEdge / w : maxEdge / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

async function createBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file); } catch (e) { console.debug("[image] createImageBitmap failed, falling back to Image:", e?.message); }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}
